/**
 * The sample chooser behind Change cohort (account panel) and the sample
 * bar's chip: which view to open, then the cohort it belongs to. A choice of
 * a few short options is a segmented control in the subject focus tabs' look
 * (paper segments, the one in force washed cobalt); Level keeps a select for
 * its ten levels; a field with one possible value reads as plain text, as the
 * focus picker's level does when there is nothing to choose. Labels sit left
 * of their controls from `sm`, above them on a phone.
 */
import { type ReactNode, useId } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { validExamLevelsForOrgLevel } from '@shared/examLevels';
import { ORG_LEVEL_DISPLAY_LABELS, ORG_LEVELS } from '@shared/orgLevels';
import {
  type SandboxCohort,
  SandboxCohortSchema,
  type SandboxView,
  SandboxViewSchema,
} from '@shared/sandbox';

import { WORKSPACE_TARGET_MIN_HEIGHT } from './targetSizes';

const VIEW_LABELS: Record<SandboxView, string> = {
  teacher: 'Teacher',
  student: 'Student',
};

/** A-Math falls back to E-Math wherever the new level or stream drops it. */
const offered = (next: SandboxCohort): SandboxCohort =>
  SandboxCohortSchema.safeParse(next).success
    ? next
    : { ...next, mathSubject: 'E-Math' };

const withLevel = (
  cohort: SandboxCohort,
  orgLevel: SandboxCohort['orgLevel']
): SandboxCohort => {
  const streams = validExamLevelsForOrgLevel(orgLevel);
  return offered({
    ...cohort,
    orgLevel,
    examLevel: streams.includes(cohort.examLevel)
      ? cohort.examLevel
      : streams[0]!,
  });
};

const segmentedSx = {
  display: 'flex',
  width: '100%',
  '& .MuiToggleButton-root': {
    flex: 1,
    minHeight: WORKSPACE_TARGET_MIN_HEIGHT,
    px: '8px',
    border: '1px solid var(--border)',
    backgroundColor: 'var(--paper)',
    color: 'var(--ink-muted)',
    font: 'inherit',
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: '-0.01em',
    textTransform: 'none',
    whiteSpace: 'nowrap',
    transition:
      'background-color 200ms var(--ease-default, ease-out), border-color 200ms var(--ease-default, ease-out), color 200ms var(--ease-default, ease-out)',
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  },
  '& .MuiToggleButton-root.Mui-selected': {
    backgroundColor: 'color-mix(in srgb, var(--cobalt) 10%, var(--paper))',
    borderColor: 'var(--cobalt)',
    color: 'var(--ink)',
    // MUI overlaps neighbours by 1px; the one in force paints on top so its
    // cobalt edge wins the seam.
    position: 'relative',
    zIndex: 1,
  },
  '& .MuiToggleButton-root.Mui-selected:hover': {
    backgroundColor: 'color-mix(in srgb, var(--cobalt) 14%, var(--paper))',
  },
  '& .MuiToggleButton-root:not(.Mui-selected):hover': {
    backgroundColor: 'var(--bg-alt)',
  },
} as const;

function Field({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: ReactNode;
}) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'minmax(0, 1fr)',
          sm: '64px minmax(0, 1fr)',
        },
        alignItems: 'center',
        columnGap: 2,
        rowGap: '6px',
      }}
    >
      <Typography
        id={id}
        component="span"
        sx={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-muted)' }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
}

function Fixed({ children }: { children: ReactNode }) {
  return (
    <Typography
      component="span"
      sx={{
        display: 'flex',
        alignItems: 'center',
        minHeight: { sm: 48, md: 40 },
        fontSize: 14,
        fontWeight: 700,
        color: 'var(--ink)',
      }}
    >
      {children}
    </Typography>
  );
}

export default function SandboxViewDialog({
  open,
  busy,
  error,
  view,
  cohort,
  onViewChange,
  onCohortChange,
  onOpen,
  onStartFresh,
  onClose,
}: {
  open: boolean;
  busy: boolean;
  error: string | null;
  view: SandboxView;
  cohort: SandboxCohort;
  onViewChange: (view: SandboxView) => void;
  onCohortChange: (cohort: SandboxCohort) => void;
  onOpen: () => void;
  onStartFresh: () => void;
  onClose: () => void;
}) {
  const id = useId();
  const streams = validExamLevelsForOrgLevel(cohort.orgLevel);
  const subjects = (['E-Math', 'A-Math'] as const).filter(
    mathSubject =>
      SandboxCohortSchema.safeParse({ ...cohort, mathSubject }).success
  );
  const primary = cohort.orgLevel.startsWith('primary_');
  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!busy) onClose();
      }}
      fullWidth
      maxWidth="xs"
      aria-labelledby={`${id}-title`}
    >
      <DialogTitle
        id={`${id}-title`}
        sx={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.015em' }}
      >
        Sample workspace
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', rowGap: { xs: 2, sm: 1.5 }, pt: '4px' }}>
          <Field id={`${id}-view`} label="View">
            <ToggleButtonGroup
              exclusive
              value={view}
              onChange={(_event, next: SandboxView | null) => {
                if (next) onViewChange(next);
              }}
              aria-labelledby={`${id}-view`}
              sx={segmentedSx}
            >
              {SandboxViewSchema.options.map(option => (
                <ToggleButton key={option} value={option}>
                  {VIEW_LABELS[option]}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Field>
          <Field id={`${id}-level`} label="Level">
            <TextField
              select
              size="small"
              fullWidth
              value={cohort.orgLevel}
              onChange={event =>
                onCohortChange(
                  withLevel(
                    cohort,
                    event.target.value as SandboxCohort['orgLevel']
                  )
                )
              }
              SelectProps={{ labelId: `${id}-level` }}
              sx={{
                '& .MuiInputBase-root': {
                  minHeight: WORKSPACE_TARGET_MIN_HEIGHT,
                  fontSize: 14,
                  fontWeight: 700,
                },
              }}
            >
              {ORG_LEVELS.map(level => (
                <MenuItem key={level} value={level}>
                  {ORG_LEVEL_DISPLAY_LABELS[level]}
                </MenuItem>
              ))}
            </TextField>
          </Field>
          <Field id={`${id}-stream`} label="Stream">
            {streams.length > 1 ? (
              <ToggleButtonGroup
                exclusive
                value={cohort.examLevel}
                onChange={(_event, next: SandboxCohort['examLevel'] | null) => {
                  if (next)
                    onCohortChange(offered({ ...cohort, examLevel: next }));
                }}
                aria-labelledby={`${id}-stream`}
                sx={segmentedSx}
              >
                {streams.map(stream => (
                  <ToggleButton key={stream} value={stream}>
                    {stream}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            ) : (
              <Fixed>{cohort.examLevel}</Fixed>
            )}
          </Field>
          <Field id={`${id}-subject`} label="Subject">
            {subjects.length > 1 ? (
              <ToggleButtonGroup
                exclusive
                value={cohort.mathSubject}
                onChange={(
                  _event,
                  next: SandboxCohort['mathSubject'] | null
                ) => {
                  if (next) onCohortChange({ ...cohort, mathSubject: next });
                }}
                aria-labelledby={`${id}-subject`}
                sx={segmentedSx}
              >
                {subjects.map(subject => (
                  <ToggleButton key={subject} value={subject}>
                    {subject}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            ) : (
              <Fixed>{primary ? 'Maths' : cohort.mathSubject}</Fixed>
            )}
          </Field>
        </Box>
        <Typography
          sx={{
            mt: 2.5,
            fontSize: 13,
            lineHeight: 1.5,
            color: 'var(--ink-muted)',
          }}
        >
          Open restores saved homework and progress. Start fresh creates a new
          class and keeps your previous samples.
        </Typography>
        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions
        disableSpacing
        sx={{ px: 3, pt: 1, pb: 2.5, gap: 1, flexWrap: 'wrap' }}
      >
        <Button disabled={busy} onClick={onStartFresh}>
          Start fresh
        </Button>
        <Button variant="contained" disabled={busy} onClick={onOpen}>
          {busy ? 'Opening…' : 'Open sample'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
